/**
 * Me Mode — Profile, favorites, generation history
 *
 * Self-registers with router. On activate:
 *  - Renders profile card, favorites grid, recent generations
 *  - Reuses favorites module + history-modal data
 */

import { store } from '../../core/store.js'
import { eventBus, EVENTS } from '../../core/event-bus.js'
import { registerMode } from '../../core/router.js'
import { CONFIG } from '../../core/config.js'
import { createLogger } from '../../utils/logger.js'
import { getFavorites } from './favorites/index.js'
import { fetchGenerationHistory } from '../../api/prompts.js'
import { esc, escAttr, safeImageUrl } from '../../utils/escape.js'
import { renderEmptyState, renderLoadingSkeleton } from '../page-frame/page-frame.js'
import { signOut } from '../auth/index.js'

const log = createLogger('me')

let initialized = false

// ---- Render helpers ----

const MAMAMIYA_LOGO_INLINE = `
  <svg viewBox="0 0 344 232" aria-hidden="true">
    <g fill="var(--brand-primary)">
      <path d="M108,8H76A32,32,0,0,0,76,72H108Z"/>
      <path d="M108,8H140A32,32,0,0,1,140,72H108Z"/>
      <path d="M108,72H76A32,32,0,0,0,76,136H108Z"/>
      <circle cx="140" cy="104" r="32"/>
      <path d="M108,136H72A36,36,0,1,0,108,172Z"/>
      <path d="M236,8H268A32,32,0,0,1,268,72H236Z"/>
      <path d="M236,8H204A32,32,0,0,0,204,72H236Z"/>
      <path d="M236,72H268A32,32,0,0,1,268,136H236Z"/>
      <circle cx="204" cy="104" r="32"/>
      <path d="M236,136H272A36,36,0,1,1,236,172Z"/>
    </g>
  </svg>
`

function renderProfileCard(customer) {
  if (!customer) {
    return `
      <div class="me-hero-guest">
        <span class="me-brand">
          ${MAMAMIYA_LOGO_INLINE}
          <span class="me-brand-wordmark">mamamiya</span>
        </span>
        <h2 class="me-headline">Make it yours</h2>
        <p class="me-headline-sub">Sign in to save your designs, sync across devices, and pick up where you left off.</p>
        <div class="me-cta-row">
          <button class="me-hero-cta me-hero-cta--primary" id="meLoginBtn">
            <i class="ph ph-sign-in"></i>
            <span>Sign in</span>
          </button>
          <button class="me-hero-cta me-hero-cta--ghost" id="meRegisterBtn">
            <i class="ph ph-user-plus"></i>
            <span>Create account</span>
          </button>
        </div>
      </div>`
  }

  const avatar = safeImageUrl(customer.avatar_url || customer.picture)
  const name = customer.display_name || customer.name || customer.email?.split('@')[0] || 'User'
  const email = customer.email || ''
  const credits = store.getState('credits.balance') || 0

  return `
    <div class="me-profile-v2">
      <div class="me-profile-avatar">
        ${avatar ? `<img src="${escAttr(avatar)}" alt="${escAttr(name)}" />` : `<i class="ph ph-user"></i>`}
      </div>
      <div class="me-profile-info">
        <div class="me-profile-name">${esc(name)}</div>
        ${email ? `<div class="me-profile-email">${esc(email)}</div>` : ''}
        <span class="me-profile-credits">
          <i class="ph-fill ph-coin"></i>
          ${esc(credits)} credits
        </span>
      </div>
    </div>`
}

/**
 * Stage actions in the shared toolbar row (one layout across modes — the
 * Create topbar pattern, bao 2026-10-24): Sign in for guests, Sign out when
 * authed. Sign-out revokes the refresh family server-side via signOut().
 */
function renderMeToolbar(customer) {
  const action = customer
    ? `<button class="pf-action-btn" id="meLogoutBtn" type="button"><i class="ph ph-sign-out"></i><span>Sign out</span></button>`
    : `<button class="pf-action-btn" id="meLoginBtn" type="button"><i class="ph ph-sign-in"></i><span>Sign in</span></button>`
  return `
    <div class="mode-toolbar">
      <div class="mode-toolbar__left"></div>
      <div class="mode-toolbar__right">${action}</div>
    </div>`
}

function renderFavoritesSection(favorites) {
  if (!favorites || favorites.length === 0) {
    return `
      <div class="me-section">
        <h3 class="me-section__title">Favorites</h3>
        ${renderEmptyState({ icon: 'ph-heart', message: 'No favorites yet' })}
      </div>`
  }

  const items = favorites
    .slice(0, 8)
    .map((fav) => {
      const imgUrl = safeImageUrl(fav.item_image_url || fav.image_url)
      const title = fav.item_title || fav.source_title || 'Favorite'
      return `<div class="me-fav-item">
      ${imgUrl ? `<img src="${escAttr(imgUrl)}" alt="${escAttr(title)}" loading="lazy" />` : '<div class="me-fav-placeholder"></div>'}
    </div>`
    })
    .join('')

  return `
    <div class="me-section">
      <h3 class="me-section__title">Favorites <span class="me-section__count">${favorites.length}</span></h3>
      <div class="me-fav-grid">${items}</div>
    </div>`
}

function renderHistorySection(records) {
  // Records with neither a prompt nor any image render as a useless
  // "No prompt" empty card (sheet-flow rows carry no final_prompt) — drop
  // them before deciding the section is empty.
  const visible = (records || []).filter(
    (r) => String(r?.final_prompt || '').trim() || safeImageUrl(r?.image_url || r?.source_image_url)
  )
  if (!visible.length) {
    return `
      <div class="me-section">
        <h3 class="me-section__title">Recent Generations</h3>
        ${renderEmptyState({ icon: 'ph-clock-counter-clockwise', message: 'No generations yet' })}
      </div>`
  }

  const items = visible
    .slice(0, 6)
    .map((r) => {
      const thumb = safeImageUrl(r.image_url || r.source_image_url)
      const promptSnippet = String(r.final_prompt || '').substring(0, 50)
      return `<div class="me-history-item">
      <div class="me-history-thumb">${thumb ? `<img src="${escAttr(thumb)}" alt="" loading="lazy" />` : '<div class="me-history-placeholder"></div>'}</div>
      <div class="me-history-info">
        <div class="me-history-prompt">${esc(promptSnippet || 'No prompt')}</div>
      </div>
    </div>`
    })
    .join('')

  return `
    <div class="me-section">
      <h3 class="me-section__title">Recent Generations</h3>
      <div class="me-history-list">${items}</div>
    </div>`
}

// ---- Activate ----

let creditsSubscribed = false

async function activate() {
  log.info('Activating Me mode')
  const container = document.getElementById('mode-me')
  if (!container) return

  // The profile card reads credits.balance at render time, but initCredits
  // resolves async after boot — without this the avatar badge stuck at
  // "0 credits" while the header bar showed the real balance.
  if (!creditsSubscribed) {
    creditsSubscribed = true
    store.subscribe('credits.balance', (val) => {
      const el = document.querySelector('#mode-me .me-profile-credits')
      if (el) {
        el.replaceChildren()
        const icon = document.createElement('i')
        icon.className = 'ph-fill ph-coin'
        el.append(icon, document.createTextNode(` ${Number(val) || 0} credits`))
      }
    })
  }

  const customer = store.getState('auth.customer')

  // Show loading state — unified shimmer skeleton (H-130), no spinner.
  container.innerHTML = `
    ${renderMeToolbar(customer)}
    <div class="me-container">
      ${renderProfileCard(customer)}
      ${renderLoadingSkeleton({ count: 2 })}
    </div>
  `

  bindMeButtons(container)

  // Load data in parallel
  const [favorites, history] = await Promise.all([
    Promise.resolve(getFavorites()),
    customer?.id ? fetchGenerationHistory(customer.id, 6, 0).catch(() => []) : Promise.resolve([]),
  ])

  // Re-render with data
  container.innerHTML = `
    ${renderMeToolbar(customer)}
    <div class="me-container">
      ${renderProfileCard(customer)}
      ${renderFavoritesSection(favorites)}
      ${renderHistorySection(history)}
    </div>
  `

  bindMeButtons(container)

  initialized = true
}

function bindMeButtons(container) {
  const openLogin = () => {
    store.setState('auth.showLoginModal', true)
    eventBus.emit(EVENTS.AUTH_CHANGED)
  }

  const loginBtn = container.querySelector('#meLoginBtn')
  if (loginBtn) loginBtn.addEventListener('click', openLogin)

  const registerBtn = container.querySelector('#meRegisterBtn')
  if (registerBtn) registerBtn.addEventListener('click', openLogin)

  // Sign-out revokes the refresh family server-side, then clears local auth.
  // Await it and re-render — otherwise the page stays in the signed-in state
  // and the button looks dead (2026-10-28 bao: "无法 sign out").
  const logoutBtn = container.querySelector('#meLogoutBtn')
  if (logoutBtn)
    logoutBtn.addEventListener('click', async () => {
      logoutBtn.disabled = true
      await signOut().catch((err) => log.warn('Sign-out failed:', err?.message || err))
      activate()
    })
}

function deactivate() {
  log.info('Deactivating Me mode')
}

// Self-register with router
registerMode('me', { activate, deactivate })

log.info('Me mode registered')
